// Port of ios/Sources/ViewModels/DraftsPresentation.swift.
//
// The Drafts view's rows: everything unsent, across every conversation, newest first — the
// desktop's own Drafts list at mobile proportions. Pure, so the two things worth getting
// right are testable without a database: which conversation a draft belongs to, and the fact
// that a draft written in a THREAD is not the same place as one written in the conversation.
import type { DraftRecord } from './records';
import { flattenedDraft } from './channelListDerivation';
import { format as relativeTimeFormat } from './relativeTime';

/**
 * How many drafts the reader has — the number the sidebar row shows, and the test for
 * whether that row is drawn at all.
 *
 * Counted through the SAME predicate `rows` draws with, and that is the entire point.
 * Android's sidebar used to ask SQL (`SELECT COUNT(*) FROM drafts`) while its list asked the
 * Markdown flattener, so a draft SQL could see and the flattener could not — a lone space, a
 * stray newline, `**` abandoned half-typed — was counted and never listed, and the reader got
 * "Drafts 1" over a sheet saying "Nothing unsent" (reported 2026-09-21). iOS never showed the
 * number at all, so it could not tell that lie, but it could not keep the promise either.
 * One predicate, so neither platform can drift: if it cannot be DRAWN, it is not counted.
 */
export function count(drafts: readonly DraftRecord[]): number {
  return drafts.filter(hasVisibleContent).length;
}

/** Whether this draft is something the reader could actually be shown. */
function hasVisibleContent(draft: DraftRecord): boolean {
  return flattenedDraft(draft.message) !== null;
}

export interface DraftRow {
  /** `channelId + ":" + rootId`. */
  id: string;
  channelId: string;
  /**
   * Empty for a conversation's own draft; a thread's root id for a reply. Both are
   * needed to open it in the right place.
   */
  rootId: string;
  conversationName: string;
  /** What lets the row SAY it is a reply — this list is read out of any context at all. */
  isThreadReply: boolean;
  preview: string;
  timeLabel: string;
}

/**
 * `conversationNames`: channel id → the name the reader knows it by. A draft in a
 * conversation this device has not cached still gets a row: the text is the point, and
 * `unknownConversationLabel` is a better answer than dropping something the reader typed.
 * The DAO answers newest-first and this does not re-sort: two places deciding "newest"
 * is two places that can disagree.
 */
export function rows(args: {
  drafts: readonly DraftRecord[];
  conversationNames: ReadonlyMap<string, string>;
  now: number;
  yesterdayLabel: string;
  unknownConversationLabel: string;
  timeZone?: string;
}): DraftRow[] {
  const out: DraftRow[] = [];
  for (const draft of args.drafts) {
    // The same flattening the sidebar's draft line does — whitespace is not unsent work.
    const preview = flattenedDraft(draft.message);
    if (preview === null) continue;
    out.push({
      id: draft.channelId + ':' + draft.rootId,
      channelId: draft.channelId,
      rootId: draft.rootId,
      conversationName: args.conversationNames.get(draft.channelId) ?? args.unknownConversationLabel,
      isThreadReply: draft.rootId !== '',
      preview,
      timeLabel: relativeTimeFormat(draft.updatedAt, args.now, { yesterday: args.yesterdayLabel }, args.timeZone),
    });
  }
  return out;
}

/** Namespace form, for `DraftsPresentation.rows(...)` call sites. */
export const DraftsPresentation = { count, rows } as const;
